// src/components/common/MemberMatchPicker.tsx
/**
 * Resolves one person on an intake or care request to an existing member before approval.
 *
 * The problem it solves: approval auto-matches on contact details, so someone who signed up with a
 * different email or phone than the one on their member record gets a **second** member created. This
 * shows the ranked candidates the backend can see and lets the admin say which one is really them.
 *
 * ⚠️ **The default is "let the backend decide", and that is deliberate.** `participantMembers` /
 * `subjectMembers` are optional on both approve payloads, and anyone the admin has not touched keeps
 * today's auto match-or-create. So an untouched picker contributes **nothing** to the payload, which is
 * what makes adding this a no-op for every existing flow. Do not "helpfully" pre-select a candidate:
 * that would silently start overriding a decision the backend used to make.
 *
 * Deliberately service-agnostic — it takes a `fetchCandidates` function rather than a service, because
 * `RegistrationIntakeService` and `CareRequestService` both expose `candidateMatches` with the same
 * signature and neither should be imported here.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Badge, Card, Group, Loader, Radio, Stack, Text,
} from '@mantine/core';
import { IconAlertTriangle, IconUserPlus, IconWand } from '@tabler/icons-react';
import type { MemberCandidateQuery, MemberMatchCandidate } from '@actschurch/ccms-sdk';

/** What the admin chose for this person. `null` means "untouched — leave it to the backend". */
export type MemberChoice = { memberID: number } | { createNew: true } | null;

/** Sentinel radio values. Real choices use the numeric member id as a string. */
const AUTO = 'auto';
const CREATE_NEW = 'new';

export interface MemberMatchPickerProps {
  /** Who is being resolved. Shown in the heading. */
  personName: string;
  /** Passed straight to `candidateMatches`. */
  query: MemberCandidateQuery;
  /** `intakeService.candidateMatches` or `requestService.candidateMatches`. */
  fetchCandidates: (query: MemberCandidateQuery) => Promise<MemberMatchCandidate[]>;
  value: MemberChoice;
  onChange: (choice: MemberChoice) => void;
}

/** "exact-email" → "Same email". Falls back to the raw reason so an unknown one still says something. */
function reasonLabel(reason?: string | null): string {
  switch (reason) {
    case 'exact-email': return 'Same email';
    case 'exact-phone': return 'Same phone';
    case 'exact-name': return 'Same name';
    default: return reason || 'Possible match';
  }
}

export default function MemberMatchPicker({
  personName, query, fetchCandidates, value, onChange,
}: MemberMatchPickerProps) {
  const [candidates, setCandidates] = useState<MemberMatchCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  /**
   * ⚠️ Depends on the query's **primitive fields**, never the object itself.
   *
   * Callers build `query={{ ... }}` inline inside a `.map`, so the object identity changes on every
   * render. Depending on it would refire this effect every render, and each fetch's `setState` would
   * trigger the next render: an infinite request loop against the API. Spreading the fields out means
   * the lookup reruns only when the person being resolved actually changes.
   */
  const { firstName, lastName, email, phone } = query;

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await fetchCandidates({ firstName, lastName, email, phone });
      // Strongest first: contact matches (100) above name-only ones (70).
      setCandidates([...(res ?? [])].sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0)));
    } catch (e) {
      console.error('[MemberMatchPicker] candidate lookup failed:', e);
      setCandidates([]);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [fetchCandidates, firstName, lastName, email, phone]);

  useEffect(() => { load(); }, [load]);

  const selected = value === null
    ? AUTO
    : 'createNew' in value
      ? CREATE_NEW
      : String(value.memberID);

  const handleChange = (next: string) => {
    if (next === AUTO) { onChange(null); return; }
    if (next === CREATE_NEW) { onChange({ createNew: true }); return; }
    onChange({ memberID: Number(next) });
  };

  return (
    <Card withBorder radius="md" padding="md">
      <Group justify="space-between" wrap="nowrap" mb="xs">
        <Text size="sm" fw={600} truncate>{personName}</Text>
        {loading && <Loader size="xs" />}
      </Group>

      {/* ⚠️ A failed lookup must not silently look like "no matches", or the admin would create a
          duplicate believing none existed. Say the check did not run. */}
      {failed && (
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={14} />} mb="xs" p="xs">
          <Text size="xs">
            Could not check for existing members. Approval will fall back to matching automatically.
          </Text>
        </Alert>
      )}

      <Radio.Group value={selected} onChange={handleChange}>
        <Stack gap={6}>
          <Radio
            value={AUTO}
            label={
              <Group gap={6} wrap="nowrap">
                <IconWand size={14} />
                <Text size="sm">Match automatically</Text>
                <Badge size="xs" variant="light" color="gray">Default</Badge>
              </Group>
            }
            description={candidates.length > 0
              ? 'The backend picks or creates the member, as it does today.'
              : 'The backend looks again at approval time and creates the person if nothing matches.'}
          />

          {candidates.map((c) => {
            const name = [c.firstName, c.lastName].filter(Boolean).join(' ').trim()
              || `Member #${c.memberID}`;
            return (
              <Radio
                key={c.memberID}
                value={String(c.memberID)}
                label={
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm">{name}</Text>
                    <Badge size="xs" variant="light" color={(c.confidence ?? 0) >= 100 ? 'teal' : 'gray'}>
                      {reasonLabel(c.matchReason)}
                    </Badge>
                    {!c.isMember && <Badge size="xs" variant="outline" color="orange">Not a member</Badge>}
                  </Group>
                }
                description={[c.email, c.cellNumber].filter(Boolean).join(' · ')
                  || `Member #${c.memberID}`}
              />
            );
          })}

          <Radio
            value={CREATE_NEW}
            label={
              <Group gap={6} wrap="nowrap">
                <IconUserPlus size={14} />
                <Text size="sm">Create a new person</Text>
              </Group>
            }
            description="Skips matching entirely. Use when none of the above is them."
          />
        </Stack>
      </Radio.Group>

      {!loading && !failed && candidates.length === 0 && (
        <Text size="xs" c="dimmed" mt="xs">
          No existing member matched this name, email or phone.
        </Text>
      )}
    </Card>
  );
}
